import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { DashboardUserService } from '../instances/dashboard-user';
import { DashboardUserConverter } from '@/converters/dashboard-user';
import {
  PutDashboardUserPasswordRequest,
  PutDashboardUserRequest,
  PutDashboardUserRequestAvatar,
} from '../request/dashboard-user';

export const useDashboardUser = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_DASHBOARD_USER', params],
    async () => {
      const [err, data] = await DashboardUserService.getDashboardUser(params);
      if (err) {
        throw err;
      }
      return {
        data: DashboardUserConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostDashboardUserMutation = () => {
  return useMutation({
    mutationKey: ['POS_DASHBOARD_USER'],
    mutationFn: async (payload: any) => {
      const [err, data] = await DashboardUserService.postDashboardUser(payload);
      if (err) {
        throw err;
      }
      return DashboardUserConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutDashboardUserAvatarMutation = () => {
  return useMutation({
    mutationKey: ['PUT_DASHBOARD_USER_AVATAR'],
    mutationFn: async ({ id, payload }: { id: string; payload: PutDashboardUserRequestAvatar }) => {
      const [err] = await DashboardUserService.putDashboardUserAvatar(id, payload);
      if (err) {
        throw err;
      }
    },
  });
};

export const useGetDashboardUserById = () => {
  return useMutation({
    mutationKey: ['GET_DASHBOARD_USER_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await DashboardUserService.getDashboardUserById(uuid);
      if (err) {
        throw err;
      }
      return DashboardUserConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const useGetDashboardUserByIdQuery = (uuid: string) => {
  const query = useQuery(
    ['GET_DASHBOARD_USER_BY_ID_QUERY', uuid],
    async () => {
      const [err, data] = await DashboardUserService.getDashboardUserById(uuid);
      if (err) {
        throw err;
      }
      return DashboardUserConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePutDashboardUserMutation = () => {
  return useMutation({
    mutationKey: ['PUT_DASHBOARD_USER'],
    mutationFn: async ({ id, payload }: { id: string; payload: PutDashboardUserRequest }) => {
      const [err] = await DashboardUserService.putDashboardUser(id, payload);
      if (err) {
        throw err;
      }
    },
  });
};

export const usePutDashboardUserPasswordMutation = () => {
  return useMutation({
    mutationKey: ['PUT_DASHBOARD_USER_PASSWORD'],
    mutationFn: async ({ id, payload }: { id: string; payload: PutDashboardUserPasswordRequest }) => {
      const [err] = await DashboardUserService.putDashboardUserPassword(id, payload);
      if (err) {
        throw err;
      }
    },
  });
};


export const useDeleteDashboardUserMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_DASHBOARD_USER'],
    mutationFn: async ({ id }: { id: string; }) => {
      const [err] = await DashboardUserService.deleteDashboardUser(id);
      if (err) {
        throw err;
      }
    },
  });
};

